/**
 * TextInput 组件
 * 提供文本输入功能，包括自动调整高度
 */
import React, { useRef, useEffect, useCallback, memo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { chatInputAtom, voiceModeAtom, chatInputFocusedAtom } from '../../../../atoms/v3/chatInputAtoms';
import { messageSendingAtom } from '../../../../atoms/v3/chatAtoms';
import { layoutModeAtom } from '../../../../atoms/v3/uiAtoms';
import { triggerChatMessageAtom } from '../../../../atoms/v3/tirggerChatMessageAtom';
import { usePasteUpload } from '../../../../hooks/v3/usePasteUpload';
import { useEntityAutocomplete } from '../../../../hooks/v3/useEntityAutocomplete';
import { EntitySelector } from './EntitySelector';

interface TextInputProps {
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
  isEmbedded?: boolean; // 是否在桌面端侧边栏中
  quickbarMode?: boolean; // 是否为 quickbar 模式（假门）
  onFocusNavigateToChat?: () => void; // quickbar 模式下点击时的导航函数
}

const TextInputComponent: React.FC<TextInputProps> = ({
  isDisabled = false,
  placeholder,
  className = "flex-1 mx-1 px-3 py-2 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-[1.25rem] text-ios-body placeholder-ios-gray dark:placeholder-ios-dark-gray focus:outline-none min-h-[2.5rem] max-h-[7.5rem] overflow-y-auto relative z-50",
  isEmbedded = false,
  quickbarMode = false,
  onFocusNavigateToChat
}) => {
  const { t } = useTranslation();
  const [input, setInput] = useAtom(chatInputAtom);
  const isSending = useAtomValue(messageSendingAtom);
  const sendChatMessage = useSetAtom(triggerChatMessageAtom);
  const voiceMode = useAtomValue(voiceModeAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const setChatInputFocused = useSetAtom(chatInputFocusedAtom);

  // 本地粘贴处理，确保在输入框中粘贴文件时优先处理
  const { handlePaste } = usePasteUpload();

  // 实体自动完成功能
  const {
    autocompleteState,
    currentSearchResult,
    textareaRef,
    handleInputChange,
    selectEntity,
    navigateSelection,
    selectCurrentItem,
    closeAutocomplete
  } = useEntityAutocomplete();

  // 触发位置状态（用于定位选择器）
  const [triggerPosition, setTriggerPosition] = useState({ x: 0, y: 0 });



  // 自动调整文本区域高度
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      if (input.trim() === '') {
        // 当内容为空时设置为最小高度
        textarea.style.height = '2.5rem';
      } else {
        // 自动调整非空内容的高度
        textarea.style.height = 'auto'; // 先重置高度
        const scrollHeight = textarea.scrollHeight;
        const minHeight = 40; // 2.5rem in pixels
        const maxHeight = 120; // 7.5rem in pixels
        const heightInPixels = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
        textarea.style.height = `${heightInPixels / 16}rem`;
      }
    }
  }, [input]); // 当输入变化时重新运行

  // 计算光标位置对应的屏幕坐标
  const getCaretPosition = useCallback(() => {
    if (!textareaRef.current) return { x: 0, y: 0 };

    const textarea = textareaRef.current;
    const { selectionStart } = textarea;

    // 创建一个临时的 div 来计算位置
    const div = document.createElement('div');
    const style = window.getComputedStyle(textarea);

    // 复制样式
    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordWrap = 'break-word';
    div.style.font = style.font;
    div.style.padding = style.padding;
    div.style.border = style.border;
    div.style.width = style.width;
    div.style.height = style.height;

    document.body.appendChild(div);

    // 设置文本内容到光标位置
    const textBeforeCaret = textarea.value.substring(0, selectionStart);
    div.textContent = textBeforeCaret;

    // 添加一个 span 来标记光标位置
    const span = document.createElement('span');
    span.textContent = '|';
    div.appendChild(span);

    // 获取 textarea 的位置
    const textareaRect = textarea.getBoundingClientRect();
    const spanRect = span.getBoundingClientRect();

    // 计算相对于 textarea 的位置
    const x = textareaRect.left + (spanRect.left - div.getBoundingClientRect().left);
    const y = textareaRect.top + (spanRect.top - div.getBoundingClientRect().top);

    document.body.removeChild(div);

    return { x, y };
  }, []);

  // 处理键盘事件 - 使用useCallback优化
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    // 处理实体自动完成的键盘导航
    if (autocompleteState.isOpen) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        navigateSelection('up');
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        navigateSelection('down');
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        selectCurrentItem();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeAutocomplete();
        return;
      }
    }

    // 原有的 Enter 发送逻辑
    if (e.key === 'Enter'
      && !( e.metaKey ||  e.ctrlKey || e.shiftKey || e.altKey) // Enter输入发送, 辅助键换行
      && !e.nativeEvent.isComposing
      && !isSending
      && !isDisabled) {
      e.preventDefault();
      e.stopPropagation(); // 阻止事件传播到全局快捷键系统

      // 使用 sendChatMessage atom 发送消息
      sendChatMessage();
    }
  }, [autocompleteState.isOpen, navigateSelection, selectCurrentItem, closeAutocomplete, isSending, isDisabled, sendChatMessage]);

  // 使用useCallback优化onChange处理函数
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;

    setInput(newValue);

    // 处理实体自动完成
    handleInputChange(newValue, cursorPosition);

    // 更新触发位置
    if (autocompleteState.isOpen) {
      const position = getCaretPosition();
      setTriggerPosition(position);
    }
  }, [setInput, handleInputChange, autocompleteState.isOpen, getCaretPosition]);

  // 处理焦点事件 - 用于键盘状态管理
  const handleFocus = useCallback(() => {
    setChatInputFocused(true);

    // 如果自动完成是打开的，更新触发位置
    if (autocompleteState.isOpen) {
      setTimeout(() => {
        const position = getCaretPosition();
        setTriggerPosition(position);
      }, 0);
    }
  }, [setChatInputFocused, autocompleteState.isOpen, getCaretPosition]);

  // 监听搜索结果变化，重新计算位置
  // NOTE: 当选择器内容变化导致高度变化时，需要重新定位以保持正确的位置
  useEffect(() => {
    if (autocompleteState.isOpen) {
      const position = getCaretPosition();
      setTriggerPosition(position);
    }
  }, [autocompleteState.isOpen, autocompleteState.searchQuery, getCaretPosition]);

  // 监听窗口大小变化，重新计算位置
  // NOTE: 窗口大小变化可能影响选择器的边界检测和定位
  useEffect(() => {
    const handleResize = () => {
      if (autocompleteState.isOpen) {
        const position = getCaretPosition();
        setTriggerPosition(position);
      }
    };

    if (autocompleteState.isOpen) {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [autocompleteState.isOpen, getCaretPosition]);

  // 监听滚动事件，重新计算位置
  // NOTE: 页面滚动时需要更新选择器位置以保持相对于输入框的正确位置
  useEffect(() => {
    const handleScroll = () => {
      if (autocompleteState.isOpen) {
        const position = getCaretPosition();
        setTriggerPosition(position);
      }
    };

    if (autocompleteState.isOpen) {
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [autocompleteState.isOpen, getCaretPosition]);

  const handleBlur = useCallback(() => {
    setChatInputFocused(false);
    // 注意：不在这里关闭自动完成，因为用户可能点击选择器
  }, [setChatInputFocused]);

  // 处理粘贴事件 - 优先处理文件，然后处理文本
  const onPaste = useCallback((e: React.ClipboardEvent) => {
    console.log('🔥 TextInput: onPaste triggered');

    // 检查是否有文件
    const items = e.clipboardData.items;
    let hasFiles = false;

    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        hasFiles = true;
        break;
      }
    }

    if (hasFiles) {
      console.log('🔥 TextInput: files detected, calling handlePaste');
      // 如果有文件，使用文件上传处理器
      handlePaste(e);
      // handlePaste 内部会调用 e.preventDefault() 如果检测到文件
    }
    // 如果没有文件，让默认的文本粘贴行为继续
  }, [handlePaste]);

  // 语音模式下隐藏文本输入框
  if (voiceMode) {
    return null;
  }

  // 根据布局模式和嵌入状态决定是否添加数据属性
  const shouldAddDataAttribute = layoutMode === 'desktop' && isEmbedded;

  // 获取国际化的 placeholder
  const localizedPlaceholder = placeholder || t('v3/chat:input.placeholder', '帮我规划一下日程吧');

  // quickbar 模式下显示假门
  if (quickbarMode) {
    return (
      <button
        onClick={onFocusNavigateToChat}
        disabled={isDisabled}
        className="w-full min-w-0 mx-1 px-3 py-2 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-[1.25rem] text-ios-body placeholder-ios-gray dark:placeholder-ios-dark-gray min-h-[2.5rem] flex items-center transition-colors hover:bg-ios-gray4 dark:hover:bg-ios-dark-gray3"
      >
        <span className="text-ios-gray dark:text-ios-dark-gray text-left whitespace-nowrap overflow-hidden text-ellipsis w-full min-w-0">
          {localizedPlaceholder}
        </span>
      </button>
    );
  }

  return (
    <>
      <textarea
        ref={textareaRef}
        value={input}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={localizedPlaceholder}
        disabled={isDisabled}
        className={className}
        data-testid="chat-input"
        {...(shouldAddDataAttribute && { 'data-desktop-chat-input': 'true' })}
      />

      {/* 实体选择器 */}
      <EntitySelector
        isOpen={autocompleteState.isOpen}
        searchResult={currentSearchResult}
        selectedIndex={autocompleteState.selectedIndex}
        onSelectEntity={selectEntity}
        onClose={closeAutocomplete}
        triggerPosition={triggerPosition}
      />
    </>
  );
};

// 使用memo包装组件以避免不必要的重渲染
export const TextInput = memo(TextInputComponent);
export default TextInput;
